import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { ordersApi, stagesApi } from "@/lib/api";
import { displayName, formatDateTime } from "@/lib/format";
import { OrderStatus, type ProductionOrderCreate } from "@/lib/types";

export default function OrdersList() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const stageFilter = params.get("stage") ? Number(params.get("stage")) : undefined;
  const statusFilter = (params.get("status") as (typeof OrderStatus)[number] | null) || undefined;

  const [creating, setCreating] = useState(false);

  const stages = useQuery({
    queryKey: ["stages", "all"],
    queryFn: () => stagesApi.list({ page_size: 100 }),
  });

  const orders = useQuery({
    queryKey: ["orders", "list", stageFilter, statusFilter],
    queryFn: () =>
      ordersApi.list({
        page_size: 100,
        stage_id: stageFilter,
        status: statusFilter,
      }),
  });

  function setFilter(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value === undefined || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="orders"
        title={t("orders.title")}
        description={t("orders.description")}
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            {t("orders.newOrder")}
          </button>
        }
      />

      <section className="card p-4 flex flex-wrap items-center gap-3">
        <Filter
          label={t("orders.filterStage")}
          value={stageFilter !== undefined ? String(stageFilter) : ""}
          onChange={(v) => setFilter("stage", v || undefined)}
          options={[
            { label: t("orders.filterAll"), value: "" },
            ...(stages.data?.items.map((s) => ({
              label: `${s.code} · ${displayName(s).slice(0, 28)}`,
              value: String(s.id),
            })) ?? []),
          ]}
        />
        <Filter
          label={t("orders.filterStatus")}
          value={statusFilter ?? ""}
          onChange={(v) => setFilter("status", v || undefined)}
          options={[
            { label: t("orders.filterAll"), value: "" },
            ...OrderStatus.map((s) => ({ label: s, value: s })),
          ]}
        />
        {(stageFilter !== undefined || statusFilter) && (
          <button className="btn text-xs" onClick={() => setParams({}, { replace: true })}>
            {t("orders.resetFilters")}
          </button>
        )}
      </section>

      {orders.isLoading ? (
        <LoadingState />
      ) : orders.isError ? (
        <ErrorState error={orders.error} />
      ) : orders.data!.items.length === 0 ? (
        <EmptyState title={t("orders.emptyFiltered")} />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">{t("orders.colCode")}</th>
                <th className="px-4 py-3">{t("orders.colStage")}</th>
                <th className="px-4 py-3">{t("orders.colPlan")}</th>
                <th className="px-4 py-3">{t("orders.colDone")}</th>
                <th className="px-4 py-3">{t("orders.colRejected")}</th>
                <th className="px-4 py-3">{t("orders.colStatus")}</th>
                <th className="px-4 py-3">{t("common.created")}</th>
              </tr>
            </thead>
            <tbody>
              {orders.data!.items.map((o) => (
                <tr key={o.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3">
                    <Link to={`/orders/${o.id}`} className="font-mono text-accent-strong hover:underline">
                      {o.code}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-text-muted">STAGE_{o.stage_id}</td>
                  <td className="px-4 py-3 tabular-nums">{o.target_quantity}</td>
                  <td className="px-4 py-3 tabular-nums">{o.completed_quantity}</td>
                  <td className={`px-4 py-3 tabular-nums ${o.rejected_quantity > 0 ? "text-danger" : "text-text-muted"}`}>
                    {o.rejected_quantity}
                  </td>
                  <td className="px-4 py-3"><StatusPill kind="order" value={o.status} /></td>
                  <td className="px-4 py-3 text-text-muted font-mono text-xs">{formatDateTime(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateOrderModal
          stages={stages.data?.items ?? []}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

function Filter({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-1.5 rounded-md bg-surface border border-border text-sm text-text-primary"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function CreateOrderModal({
  stages,
  onClose,
}: {
  stages: { id: number; code: string; name_ru: string; name?: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [stageId, setStageId] = useState<number | null>(stages[0]?.id ?? null);
  const [target, setTarget] = useState(10);
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (data: ProductionOrderCreate) => ordersApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      onClose();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!code.trim() || stageId === null) {
      setErr(t("orders.errCodeStage"));
      return;
    }
    mut.mutate({ code: code.trim(), stage_id: stageId, target_quantity: target });
  }

  return (
    <div
      className="fixed inset-0 bg-overlay/40 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="card p-6 w-full max-w-md space-y-4"
      >
        <h2 className="text-xl">{t("orders.modalTitle")}</h2>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("orders.codeLabel")}</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="PO_1"
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("orders.stageLabel")}</span>
          <select
            value={stageId ?? ""}
            onChange={(e) => setStageId(Number(e.target.value))}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>{s.code} · {displayName(s).slice(0, 40)}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("orders.targetLabel")}</span>
          <input
            type="number"
            min={0}
            value={target}
            onChange={(e) => setTarget(Number(e.target.value))}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border tabular-nums"
          />
        </label>
        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
            {mut.isPending ? t("common.creating") : t("common.create")}
          </button>
        </div>
      </form>
    </div>
  );
}
